import { router, usePage } from '@inertiajs/react'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Link2,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisit,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { QuickSelectDatePickerInput } from '@/shared/ui/quick-select-date-picker-input'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  reportsMaintenanceLogsCsvPath,
  reportsMaintenanceLogsPath,
  reportsMaintenanceLogsPdfPath,
} from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { TaskFilter } from '@/shared/ui/task-filter'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Separator } from '@/shared/ui/separator'
import type WebMaintenanceLogsInstrumentRow from '@/shared/types/reports/WebMaintenanceLogsInstrumentRow'
import type { WebInstrumentCalendarView } from '@/shared/types/web'
import { ReportEmptyState } from '../components/ReportEmptyState'
import InstrumentCalendarViewPanel from '@/pages/Instruments/components/ReportsTab/InstrumentCalendarViewPanel'
import QaAlertBadge from '../components/QaAlertBadge'
import MaintenanceLogsActivityFeed from './MaintenanceLogsActivityFeed'
import type WebMaintenanceLogsActivityFeedItem from '@/shared/types/web/WebMaintenanceLogsActivityFeedItem'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  formatCalendarDate,
  formatMonthYearLabel,
  parseCalendarDate,
} from '@/shared/lib/tenantDateTime'
import { deriveKpiCounts } from '@/shared/lib/maintenanceLogsKpiCounts'
import { fetchInertiaPartial } from '@/shared/lib/fetchInertiaPartial'
import { QA_ALERT_CATEGORY_MAP } from '@/shared/constants/qaAlertConfig'
import {
  MAINTENANCE_LOGS_ALL_SENTINEL,
  buildMaintenanceLogsFilterParams,
} from '@/shared/lib/maintenanceLogsFilterParams'
import {
  buildOrgCascadeChangeHandlers,
  orgSelectionCsvParams,
  visibleDepartmentOptionsForSites,
  visibleUnitOptionsForParents,
  type OrgCascadeSelection,
} from '@/shared/lib/orgMultiSelectCascade'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { clearLocationFilters, writeLocationFilters } from '@/shared/lib/locationFiltersSession'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import { downloadExport } from '@/shared/lib/downloadExport'
import InstrumentPageJump from './InstrumentPageJump'
import { formatMaintenanceLogsInstrumentOptionLabel } from './formatMaintenanceLogsInstrumentOptionLabel'
import { visibleLotDataFieldValues } from './visibleLotDataFieldValues'

interface SelectOption {
  value: string
  label: string
}

interface OrgOption {
  value: number
  label: string
  siteId?: number
  departmentId?: number
}

interface FiltersShape {
  instrumentId?: string
  fromDate?: string
  toDate?: string
  hideEmptyData?: string | boolean
  instrumentTypeIds?: number[]
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  calTaskIds?: number[]
}

interface SummaryShape {
  instrumentLabel: string
  serialNumber: string | null
  avgCompliancePercent: number | null
}

interface LotDetailShape {
  id: number
  lotNumber: string | null
  createdOn: string | null
  expirationDate: string | null
  dateReceived: string | null
  dateOpened: string | null
  openedExpiration: string | null
  dateInUse: string | null
  dateDiscarded: string | null
  manufacturer: string | null
  product: string | null
  otherIdentifier: string | null
  description: string | null
  expiresSoon: boolean
  lotTaskDataFields: LotTaskDataField[]
}

interface LotGroupShape {
  lotId: number
  lotName: string
  lotDetails: LotDetailShape[]
}

interface LotTaskDataField {
  taskName: string
  lotName: string
  dataFieldLabel: string
  dataFieldType: string
  existingValues: Array<{
    value: string | null
    isAcceptable: boolean
    operatorType: string | null
  }>
}

interface Props {
  instruments: WebMaintenanceLogsInstrumentRow[]
  selectedInstrumentId: number | null
  summary: SummaryShape | null
  reportsCalendar: WebInstrumentCalendarView | null
  // All calendar months pre-fetched for the selected date range. Used to build the
  // client-side calendarByMonth cache so ‹/› navigation requires zero extra BE requests.
  reportsCalendarMonths: WebInstrumentCalendarView[]
  currentLots: LotGroupShape[] | null
  filters: FiltersShape
  defaultFromDate: string
  defaultToDate: string
  instrumentTypeOptions: SelectOption[]
  taskNameOptions: SelectOption[]
  siteOptions: OrgOption[]
  departmentOptions: OrgOption[]
  unitOptions: OrgOption[]
  activityFeed: WebMaintenanceLogsActivityFeedItem[]
  [key: string]: unknown
}

const ALL_SENTINEL = MAINTENANCE_LOGS_ALL_SENTINEL

// ── Lot detail modal field ────────────────────────────────────────────────────

const LotField = ({
  label,
  value,
  date = false,
  urgent = false,
}: {
  label: string
  value: string | null | undefined
  date?: boolean
  urgent?: boolean
}) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`font-medium ${urgent ? 'text-destructive' : ''}`}>
      {value ? (date ? formatCalendarDate(value) : value) : '—'}
    </p>
  </div>
)

// ── Calendar month cache helpers ──────────────────────────────────────────────

/**
 * Indexes calendar month payloads by their YYYY-MM key (derived from defaultFilters.fromDate).
 * Enables O(1) client-side month switching after a single full-range fetch on page load.
 */
const buildCalendarByMonthMap = (
  months: WebInstrumentCalendarView[],
): Map<string, WebInstrumentCalendarView> => {
  const map = new Map<string, WebInstrumentCalendarView>()
  for (const month of months) {
    map.set(month.defaultFilters.fromDate.slice(0, 7), month)
  }
  return map
}

const calendarMonthMapsMatch = (
  previousMap: Map<string, WebInstrumentCalendarView>,
  nextMap: Map<string, WebInstrumentCalendarView>,
): boolean => {
  if (previousMap.size !== nextMap.size) return false
  for (const [monthKey, monthData] of nextMap) {
    if (previousMap.get(monthKey) !== monthData) return false
  }
  return true
}

// ── Main page component ──────────────────────────────────────────────────────

const INERTIA_PARTIAL_COMPONENT = 'Reports/MaintenanceLogs/Index'

// Task ticks reload the calendar only. The page stays mounted so the Task popover does not close.
const CALENDAR_TASK_FILTER_PARTIAL_PROPS = [
  'reportsCalendar',
  'reportsCalendarMonths',
  'filters',
] as const

const calendarTaskIdsKey = (taskIds: number[] | null | undefined): string =>
  (taskIds ?? []).join(',')

type FilterOverrides = Record<string, string | undefined>

const MaintenanceLogsReportPage = () => {
  const {
    instruments,
    selectedInstrumentId,
    summary,
    reportsCalendarMonths,
    currentLots,
    filters,
    defaultFromDate,
    defaultToDate,
    instrumentTypeOptions,
    taskNameOptions,
    siteOptions,
    departmentOptions,
    unitOptions,
    activityFeed,
    complianceSettings,
    currentTenantId,
    currentUserInfo,
  } = usePage<Props & SharedProps>().props
  const { version: pageVersion } = usePage()
  const currentUserId = currentUserInfo?.id ?? null

  useBreadcrumbs([{ label: t('nav.maintenanceLogsReport') }])

  const [fromDate, setFromDate] = useState(filters.fromDate ?? defaultFromDate)
  const [toDate, setToDate] = useState(filters.toDate ?? defaultToDate)
  const [hideEmptyData, setHideEmptyData] = useState(Boolean(filters.hideEmptyData))
  // Client-side cache of all pre-fetched calendar months keyed by YYYY-MM.
  // The ‹/› month navigation buttons switch calendarCurrentMonth without any BE request.
  const [calendarByMonth, setCalendarByMonth] = useState<Map<string, WebInstrumentCalendarView>>(
    () => buildCalendarByMonthMap(reportsCalendarMonths),
  )
  const [calendarCurrentMonth, setCalendarCurrentMonth] = useState(() =>
    (filters.fromDate ?? defaultFromDate).slice(0, 7),
  )
  const [lotSummaryOpen, setLotSummaryOpen] = useState(true)
  const [lotDetailModal, setLotDetailModal] = useState<
    (LotDetailShape & { lotName: string }) | null
  >(null)
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(() =>
    (filters.siteIds ?? []).map((id) => Number(id)),
  )
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>(() =>
    (filters.departmentIds ?? []).map((id) => Number(id)),
  )
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>(() =>
    (filters.unitIds ?? []).map((id) => Number(id)),
  )
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>(() =>
    (filters.instrumentTypeIds ?? []).map((id) => Number(id)),
  )
  const calFilterTaskControlId = useId()
  const instrumentSelectControlId = useId()
  const [selectedCalTaskIds, setSelectedCalTaskIds] = useState<number[]>(
    () => filters.calTaskIds ?? [],
  )
  // History toggle: alerts-only by default (legacy parity, REBUILD-603). Not persisted, so a
  // reload or a new visit starts alerts-only again. Filter ticks keep the page mounted and can
  // make the server pick another instrument, so reset whenever the selected instrument changes.
  const [alertOnly, setAlertOnly] = useState(true)
  const [alertOnlyInstrumentId, setAlertOnlyInstrumentId] = useState(selectedInstrumentId)
  if (alertOnlyInstrumentId !== selectedInstrumentId) {
    setAlertOnlyInstrumentId(selectedInstrumentId)
    setAlertOnly(true)
  }
  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()
  // True while the latest calendar reload came from a task tick. Other reloads still jump
  // back to the first month of a new range.
  const isTaskFilterReloadRef = useRef(false)
  // Overrides of a full filter visit still in flight. A task tick interrupts that visit, so
  // the tick must resend them and reload every prop, or the instrument list and summary stay stale.
  const pendingFullVisitOverridesRef = useRef<FilterOverrides | null>(null)

  const noteNonTaskCalendarReload = () => {
    isTaskFilterReloadRef.current = false
  }

  // Sync the Task filter from server filters only — keep separate from other filter sync so an
  // in-flight "Show All" is not overwritten by unrelated prop churn. Keyed on the joined ids
  // (not the array reference) so an inline [] cannot retrigger the sync.
  const serverCalTaskIdsKey = calendarTaskIdsKey(filters.calTaskIds)

  // Rebuild the month cache when the server sends fresh data (full page navigation).
  // Inertia preserves the component instance across navigations so useEffect is the only
  // way to sync prop changes back into local state without remounting the whole page.

  useEffect(() => {
    const newMap = buildCalendarByMonthMap(reportsCalendarMonths)
    setCalendarByMonth((previousMap) =>
      calendarMonthMapsMatch(previousMap, newMap) ? previousMap : newMap,
    )
    if (isTaskFilterReloadRef.current) {
      // A task tick must not change the panel key (instrument + viewed month).
      setCalendarCurrentMonth((currentMonth) => {
        if (newMap.has(currentMonth)) return currentMonth
        const firstKey = [...newMap.keys()].sort()[0]
        return firstKey ?? currentMonth
      })
      return
    }
    // Reset to the first month of the new range so the panel always shows a valid month.
    const firstKey = [...newMap.keys()].sort()[0]
    if (firstKey) setCalendarCurrentMonth(firstKey)
  }, [reportsCalendarMonths])

  useEffect(() => {
    setFromDate(filters.fromDate ?? defaultFromDate)
    setToDate(filters.toDate ?? defaultToDate)
    setHideEmptyData(Boolean(filters.hideEmptyData))
    setSelectedSiteIds((filters.siteIds ?? []).map((id) => Number(id)))
    setSelectedDepartmentIds((filters.departmentIds ?? []).map((id) => Number(id)))
    setSelectedUnitIds((filters.unitIds ?? []).map((id) => Number(id)))
    setSelectedTypeIds((filters.instrumentTypeIds ?? []).map((id) => Number(id)))
  }, [
    filters.fromDate,
    filters.toDate,
    filters.hideEmptyData,
    filters.siteIds,
    filters.departmentIds,
    filters.unitIds,
    filters.instrumentTypeIds,
    defaultFromDate,
    defaultToDate,
  ])

  // The server drops task ids that are not the selected instrument's own, so a filter change
  // that brings another instrument also resyncs here, even when a cancelled task tick never
  // updated the echoed key.
  useEffect(() => {
    setSelectedCalTaskIds(filters.calTaskIds ?? [])
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on value identity, not array ref
  }, [serverCalTaskIdsKey, selectedInstrumentId])

  const calFilterTaskOptions = useMemo(
    () => taskNameOptions.map((option) => ({ id: Number(option.value), name: option.label })),
    [taskNameOptions],
  )

  const instrumentSelectValue =
    selectedInstrumentId != null ? String(selectedInstrumentId) : ALL_SENTINEL

  const buildFilterParams = (
    overrides: FilterOverrides = {},
    calTaskIds: number[] = selectedCalTaskIds,
  ) =>
    buildMaintenanceLogsFilterParams({
      fromDate,
      toDate,
      hideEmptyData,
      selectedTypeIds,
      selectedSiteIds,
      selectedDepartmentIds,
      selectedUnitIds,
      calTaskIds,
      selectedInstrumentId,
      overrides,
    })

  const clearPendingFullVisit = (visit: FilterVisit) => {
    if (visit.isCurrent()) pendingFullVisitOverridesRef.current = null
  }

  const navigateWithFilters = (
    overrides: FilterOverrides = {},
    mode: FilterVisitMode = 'default',
  ) => {
    // Persist effective location filters: if the override key is present (even undefined =
    // cleared), use it; otherwise fall back to current state (non-location navigate call).
    const parseCsvIds = (csv: string | undefined): number[] =>
      csv
        ? csv
            .split(',')
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0)
        : []
    writeLocationFilters(currentTenantId, currentUserId, {
      siteIds: 'site_ids' in overrides ? parseCsvIds(overrides.site_ids) : selectedSiteIds,
      departmentIds:
        'department_ids' in overrides
          ? parseCsvIds(overrides.department_ids)
          : selectedDepartmentIds,
      unitIds: 'unit_ids' in overrides ? parseCsvIds(overrides.unit_ids) : selectedUnitIds,
    })
    const params = buildFilterParams(overrides)
    const visit = beginVisit(
      JSON.stringify(params),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    noteNonTaskCalendarReload()
    if (!params.cal_task_ids) {
      setSelectedCalTaskIds((currentTaskIds) => (currentTaskIds.length === 0 ? currentTaskIds : []))
    }
    pendingFullVisitOverridesRef.current = overrides
    router.get(reportsMaintenanceLogsPath(), params, {
      preserveState: visit.keepDropdownOpen,
      ...(visit.keepDropdownOpen ? { preserveScroll: true } : {}),
      onFinish: () => clearPendingFullVisit(visit),
    })
  }

  const handleHideEmptyDataChange = (checked: boolean) => {
    setHideEmptyData(checked)
    navigateWithFilters({ hide_empty_data: checked ? '1' : undefined }, 'commit')
  }

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions, selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  const filteredUnitOptions = useMemo(
    () => visibleUnitOptionsForParents(unitOptions, selectedSiteIds, selectedDepartmentIds),
    [unitOptions, selectedSiteIds, selectedDepartmentIds],
  )

  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions,
    unitOptions,
    selection: {
      siteIds: selectedSiteIds,
      departmentIds: selectedDepartmentIds,
      unitIds: selectedUnitIds,
    },
    applySelection: ({ siteIds, departmentIds, unitIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDepartmentIds(departmentIds)
      setSelectedUnitIds(unitIds)
    },
  })

  const navigateWithOrgSelection = (selection: OrgCascadeSelection) =>
    navigateWithFilters(orgSelectionCsvParams(selection), 'commit')

  const handleSiteIdsCommit = (selected: number[]) =>
    navigateWithOrgSelection(orgCascade.changeSites(selected))

  const handleDepartmentIdsCommit = (selected: number[]) =>
    navigateWithOrgSelection(orgCascade.changeDepartments(selected))

  const closeSheetWithCascadePrune = () =>
    navigateWithFilters(orgSelectionCsvParams(orgCascade.pruneSelection()), 'sheet-close')

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    hasUrlLocationParams:
      (filters.siteIds?.length ?? 0) > 0 ||
      (filters.departmentIds?.length ?? 0) > 0 ||
      (filters.unitIds?.length ?? 0) > 0,
    currentFilters: {
      siteIds: (filters.siteIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
      departmentIds: (filters.departmentIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
      unitIds: (filters.unitIds ?? []).map((id) => Number(id)).filter((id) => id > 0),
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDepartmentIds(hydrated.departmentIds)
      setSelectedUnitIds(hydrated.unitIds)
      navigateWithFilters(
        orgSelectionCsvParams({
          siteIds: hydrated.siteIds,
          departmentIds: hydrated.departmentIds,
          unitIds: hydrated.unitIds,
        }),
      )
    },
  })

  const handleUnitIdsCommit = (selected: number[]) => {
    setSelectedUnitIds(selected)
    navigateWithFilters(
      { unit_ids: selected.length > 0 ? selected.join(',') : undefined },
      'commit',
    )
  }

  const handleTypeIdsCommit = (selected: number[]) => {
    setSelectedTypeIds(selected)
    // Changing the types resets the instrument: an id that is still in scope would otherwise
    // survive (selectedInstrumentId is in the builder) and the report body would not change.
    // Dropping it lets the backend reselect the first matching instrument, as on first load
    // (REBUILD-497).
    navigateWithFilters(
      {
        instrument_type_ids: selected.length > 0 ? selected.join(',') : undefined,
        instrument_id: undefined,
      },
      'commit',
    )
  }

  const clearFilters = () => {
    noteNonTaskCalendarReload()
    setHideEmptyData(false)
    setSelectedSiteIds([])
    setSelectedDepartmentIds([])
    setSelectedUnitIds([])
    setSelectedTypeIds([])
    clearLocationFilters(currentTenantId, currentUserId)
    router.get(reportsMaintenanceLogsPath(), {}, { preserveState: false })
  }

  const onInstrumentChange = (value: string) => {
    noteNonTaskCalendarReload()
    setAlertOnly(true)
    // The Task filter is scoped to the previously selected instrument's own tasks.
    // Carrying it over on an instrument switch silently filters by stale, no-longer-relevant
    // ids. Clear it in place and via override so the request never resends them either.
    setSelectedCalTaskIds([])
    const nextId = value === ALL_SENTINEL ? undefined : value
    router.get(
      reportsMaintenanceLogsPath(),
      buildFilterParams({
        instrument_id: nextId,
        cal_task_ids: undefined,
      }),
      { preserveState: false },
    )
  }

  const currentIndex =
    selectedInstrumentId != null ? instruments.findIndex((i) => i.id === selectedInstrumentId) : -1

  const prevInstrument = currentIndex > 0 ? instruments[currentIndex - 1] : null
  const nextInstrument =
    currentIndex >= 0 && currentIndex < instruments.length - 1
      ? instruments[currentIndex + 1]
      : null

  // Delegates to onInstrumentChange so prev/next navigation clears the Task
  // calendar filter the same way the instrument select does (see there for why).
  const navigateToInstrument = (instrument: WebMaintenanceLogsInstrumentRow) =>
    onInstrumentChange(String(instrument.id))

  // A newer sync visit interrupts this one in Inertia, so a slower response for an older
  // selection never lands. The shared guard also lets a later sheet close skip this selection.
  const onCalTaskIdsApply = (taskIds: number[]) => {
    const pendingFullVisitOverrides = pendingFullVisitOverridesRef.current
    // Pass the new ids directly: React state has not re-rendered yet. Building them in the
    // usual key order keeps the dedupe key equal to a later sheet close's.
    const params = buildFilterParams(
      { ...pendingFullVisitOverrides, cal_task_ids: calendarTaskIdsKey(taskIds) },
      taskIds,
    )
    const visit = beginVisit(JSON.stringify(params), 'commit')
    if (!visit) return
    // A tick that takes over a full visit (for example a new date range) lands like that visit.
    isTaskFilterReloadRef.current = pendingFullVisitOverrides === null
    setSelectedCalTaskIds(taskIds)
    // preserveState keeps the Task popover mounted. Without a pending full visit, only the
    // calendar props reload.
    router.get(reportsMaintenanceLogsPath(), params, {
      preserveState: true,
      preserveScroll: true,
      ...(pendingFullVisitOverrides === null
        ? { only: [...CALENDAR_TASK_FILTER_PARTIAL_PROPS] }
        : {}),
      onFinish: () => clearPendingFullVisit(visit),
    })
  }

  const navigateWithDateRange = (from: string, to: string) =>
    navigateWithFilters({ from_date: from, to_date: to })

  // Page-level date picker handlers. Apply immediately when the range is valid.
  const handleDateFromChange = (value: string) => {
    setFromDate(value)
    if (value && toDate && value <= toDate) navigateWithDateRange(value, toDate)
  }

  const handleDateToChange = (value: string) => {
    setToDate(value)
    if (fromDate && value && fromDate <= value) navigateWithDateRange(fromDate, value)
  }

  // Client-side month navigation — no BE request, just flips the active month in the cache.
  const calendarMonthKeys = useMemo(() => [...calendarByMonth.keys()].sort(), [calendarByMonth])
  const calendarMonthIdx = calendarMonthKeys.indexOf(calendarCurrentMonth)
  const canGoPrevCalendarMonth = calendarMonthIdx > 0
  const canGoNextCalendarMonth = calendarMonthIdx < calendarMonthKeys.length - 1

  const handlePrevCalendarMonth = () => {
    if (!canGoPrevCalendarMonth) return
    setCalendarCurrentMonth(calendarMonthKeys[calendarMonthIdx - 1])
  }

  const handleNextCalendarMonth = () => {
    if (!canGoNextCalendarMonth) return
    setCalendarCurrentMonth(calendarMonthKeys[calendarMonthIdx + 1])
  }

  const handleExportPdf = () => {
    if (selectedInstrumentId == null) return
    const queryParams = buildFilterParams()
    const queryString = new URLSearchParams(queryParams).toString()
    void downloadExport(
      `${reportsMaintenanceLogsPdfPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  const handleExportCsv = () => {
    if (selectedInstrumentId == null) return
    const queryParams = buildFilterParams()
    const queryString = new URLSearchParams(queryParams).toString()
    void downloadExport(
      `${reportsMaintenanceLogsCsvPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const activeFilterCount = [
    filters.instrumentId,
    selectedSiteIds.length > 0,
    selectedDepartmentIds.length > 0,
    selectedUnitIds.length > 0,
    selectedTypeIds.length > 0,
    hideEmptyData,
  ].filter(Boolean).length

  // Partial Inertia fetch for same-month calendar refresh (post-review, task filter change).
  // Month navigation is fully client-side: the ‹/› buttons flip calendarCurrentMonth using
  // the pre-fetched calendarByMonth cache — no BE request is issued.
  const fetchCalendarData = useCallback(
    (from: string, _to: string, taskIds: number[]): Promise<WebInstrumentCalendarView | null> => {
      const filterQueryParams = buildMaintenanceLogsFilterParams({
        fromDate,
        toDate,
        hideEmptyData,
        selectedTypeIds,
        selectedSiteIds,
        selectedDepartmentIds,
        selectedUnitIds,
        calTaskIds: selectedCalTaskIds,
        selectedInstrumentId,
        overrides: {
          cal_from: from,
          cal_task_ids: taskIds.length > 0 ? taskIds.join(',') : undefined,
        },
      })
      const url = new URL(reportsMaintenanceLogsPath(), window.location.origin)
      Object.entries(filterQueryParams).forEach(([key, value]) => {
        url.searchParams.set(key, value)
      })

      return fetchInertiaPartial<{ reportsCalendar: WebInstrumentCalendarView }>(url, {
        component: INERTIA_PARTIAL_COMPONENT,
        only: ['reportsCalendar'],
        version: pageVersion,
      }).then((props) => {
        const data = props?.reportsCalendar ?? null
        if (data) {
          // Update cache so KPI counts reflect post-review or task-filter state.
          const monthKey = from.slice(0, 7)
          setCalendarByMonth((prev) => {
            const next = new Map(prev)
            next.set(monthKey, data)
            return next
          })
        }
        return data
      })
    },

    [
      selectedInstrumentId,
      fromDate,
      toDate,
      hideEmptyData,
      selectedTypeIds,
      selectedSiteIds,
      selectedDepartmentIds,
      selectedUnitIds,
      selectedCalTaskIds,
      pageVersion,
    ],
  )

  const orgFilters = (
    <>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.maintenanceLogs.filterSite')}
        </Label>
        <MultiSelectCombobox
          options={siteOptions.map((option) => ({ value: option.value, label: option.label }))}
          selected={selectedSiteIds}
          onChange={orgCascade.changeSites}
          onClose={handleSiteIdsCommit}
          placeholder={t('reports.maintenanceLogs.allSites')}
          className="w-full lg:w-auto lg:min-w-40 lg:max-w-64"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.maintenanceLogs.filterDepartment')}
        </Label>
        <MultiSelectCombobox
          options={filteredDepartmentOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedDepartmentIds}
          onChange={orgCascade.changeDepartments}
          onClose={handleDepartmentIdsCommit}
          placeholder={t('reports.maintenanceLogs.allDepartments')}
          className="w-full lg:w-auto lg:min-w-40 lg:max-w-64"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.maintenanceLogs.filterUnit')}
        </Label>
        <MultiSelectCombobox
          options={filteredUnitOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedUnitIds}
          onChange={setSelectedUnitIds}
          onClose={handleUnitIdsCommit}
          placeholder={t('reports.maintenanceLogs.allUnits')}
          className="w-full lg:w-auto lg:min-w-40 lg:max-w-64"
          showBadges={false}
        />
      </div>
    </>
  )

  const instrumentStepButtons = (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1"
        disabled={!prevInstrument}
        onClick={() => prevInstrument && navigateToInstrument(prevInstrument)}
      >
        <ChevronLeft className="h-4 w-4" />
        {t('reports.maintenanceLogs.prevInstrument')}
      </Button>
      <span className="min-w-[7rem] text-center text-xs text-muted-foreground tabular-nums">
        {t('reports.maintenanceLogs.instrumentPosition', {
          current: currentIndex >= 0 ? currentIndex + 1 : '—',
          total: instruments.length,
        })}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1"
        disabled={!nextInstrument}
        onClick={() => nextInstrument && navigateToInstrument(nextInstrument)}
      >
        {t('reports.maintenanceLogs.nextInstrument')}
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )

  // Same filter bar as the Instruments report: one wrapping row.
  // Maintenance Logs also has Unit, after Department.
  // Month navigation stays on the page-level ‹/› buttons (e47-s4).
  const filterFields = (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-start"
      data-testid="maintenance-logs-filter-fields"
    >
      {orgFilters}

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('instruments.show.reports.filters.from')}
        </Label>
        <QuickSelectDatePickerInput
          value={fromDate}
          onChange={handleDateFromChange}
          // Cannot select a start date after the current end date
          maxDate={parseCalendarDate(toDate) ?? undefined}
          className="w-full lg:w-36"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('instruments.show.reports.filters.to')}
        </Label>
        <QuickSelectDatePickerInput
          value={toDate}
          onChange={handleDateToChange}
          // Cannot select an end date before the current start date
          minDate={parseCalendarDate(fromDate) ?? undefined}
          className="w-full lg:w-36"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('reports.maintenanceLogs.filterInstrumentTypes')}
        </Label>
        <MultiSelectCombobox
          options={instrumentTypeOptions
            .filter((opt) => !Number.isNaN(Number(opt.value)))
            .map((opt) => ({
              value: Number(opt.value),
              label: opt.label,
            }))}
          selected={selectedTypeIds}
          onChange={setSelectedTypeIds}
          onClose={handleTypeIdsCommit}
          placeholder={t('reports.maintenanceLogs.allInstrumentTypes')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex items-center gap-2 self-end pb-1">
        <Checkbox
          id="hide-empty-maintenance-logs"
          checked={hideEmptyData}
          onCheckedChange={(checked) => handleHideEmptyDataChange(checked === true)}
        />
        <Label htmlFor="hide-empty-maintenance-logs" className="cursor-pointer text-sm">
          {t('reports.maintenanceLogs.hideEmptyData')}
        </Label>
      </div>
    </div>
  )

  // Derived from the cache; updates after a post-review partial fetch without a full reload.
  const localCalendarData = calendarByMonth.get(calendarCurrentMonth) ?? null
  const kpiCounts = localCalendarData ? deriveKpiCounts(localCalendarData) : null

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ── Page header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold sm:text-2xl">{t('reports.maintenanceLogs.title')}</h1>
          <p
            className="mt-1 text-sm tabular-nums text-muted-foreground"
            aria-label={`${t('reports.maintenanceLogs.monthLabel')}: ${formatMonthYearLabel(fromDate)}`}
          >
            {formatMonthYearLabel(fromDate)}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={selectedInstrumentId == null}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.maintenanceLogs.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPdf}
            disabled={selectedInstrumentId == null}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.maintenanceLogs.exportPdf')}
          </Button>

          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                // Prune on close — MultiSelect onClose may not run (e.g. Escape).
                if (!open) closeSheetWithCascadePrune()
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('reports.maintenanceLogs.filters')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('reports.maintenanceLogs.filters')}
                hideCloseButton
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-semibold">{t('reports.maintenanceLogs.filters')}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('reports.maintenanceLogs.clearFilters')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('reports.maintenanceLogs.filters')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* ── Filters (desktop) ── */}
      <div
        className="hidden rounded-md border p-4 lg:block"
        data-testid="maintenance-logs-desktop-filters"
      >
        <div className="flex flex-col gap-3">
          {filterFields}

          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('reports.maintenanceLogs.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Instrument selector ── */}
      <div className="rounded-md border p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor={instrumentSelectControlId} className="text-xs text-muted-foreground">
              {t('reports.maintenanceLogs.selectInstrument')}
            </Label>
            <SearchableSelect
              id={instrumentSelectControlId}
              className="w-full max-w-xl"
              value={instrumentSelectValue}
              onValueChange={onInstrumentChange}
              aria-label={t('reports.maintenanceLogs.selectInstrument')}
              placeholder={t('reports.maintenanceLogs.selectInstrumentPlaceholder')}
              options={[
                {
                  value: ALL_SENTINEL,
                  label: t('reports.maintenanceLogs.selectInstrumentPlaceholder'),
                },
                ...instruments.map((row, rowIndex) => ({
                  value: String(row.id),
                  label: formatMaintenanceLogsInstrumentOptionLabel(row, rowIndex),
                })),
              ]}
            />
          </div>

          <InstrumentPageJump
            instruments={instruments}
            selectedInstrumentId={selectedInstrumentId}
            onInstrumentChange={onInstrumentChange}
          />

          <div className="flex flex-col items-stretch gap-2">{instrumentStepButtons}</div>
        </div>
      </div>

      {instruments.length === 0 ? (
        <ReportEmptyState message={t('reports.maintenanceLogs.noInstruments')} />
      ) : null}

      {/* ── Calendar section (visible when instrument selected + calendar data loaded) ── */}
      {selectedInstrumentId && calendarByMonth.size > 0 && summary && (
        <div className="space-y-4 rounded-md border p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">{t('reports.maintenanceLogs.calendarTitle')}</h2>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handlePrevCalendarMonth}
                disabled={!canGoPrevCalendarMonth}
                aria-label={t('instruments.show.reports.pager.previous')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[6rem] text-center text-sm tabular-nums">
                {formatMonthYearLabel(calendarCurrentMonth)}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleNextCalendarMonth}
                disabled={!canGoNextCalendarMonth}
                aria-label={t('instruments.show.reports.pager.next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/*
            QA Alert badges (AC3) — labels from qaAlertConfig.labelKey (single SoT).
            Counts summarize the loaded calendar month; activity feed uses report
            from_date–to_date (see deriveKpiCounts JSDoc).
          */}
          {kpiCounts && (
            <div
              className="flex flex-wrap gap-2"
              role="list"
              aria-label={t('reports.maintenanceLogs.kpiBarLabel')}
            >
              <QaAlertBadge
                category="failures"
                label={t(QA_ALERT_CATEGORY_MAP.failures.labelKey)}
                count={kpiCounts.failures}
              />
              <QaAlertBadge
                category="nonCompliant"
                label={t(QA_ALERT_CATEGORY_MAP.nonCompliant.labelKey)}
                count={kpiCounts.nonCompliantEvents}
              />
              <QaAlertBadge
                category="serviceReports"
                label={t(QA_ALERT_CATEGORY_MAP.serviceReports.labelKey)}
                count={kpiCounts.serviceReports}
              />
              <QaAlertBadge
                category="dateChanges"
                label={t(QA_ALERT_CATEGORY_MAP.dateChanges.labelKey)}
                count={kpiCounts.dateChanges}
              />
              <QaAlertBadge
                category="statusChanges"
                label={t(QA_ALERT_CATEGORY_MAP.statusChanges.labelKey)}
                count={kpiCounts.statusChanges}
              />
              <QaAlertBadge
                category="departmentMoved"
                label={t(QA_ALERT_CATEGORY_MAP.departmentMoved.labelKey)}
                count={kpiCounts.departmentMoved}
              />
            </div>
          )}

          {/* Period compliance chip — label + threshold-colored ComplianceBadge */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('reports.maintenanceLogs.complianceBadge')}
            </span>
            <ComplianceBadge
              percent={summary.avgCompliancePercent}
              colorCodes={complianceSettings?.colorCodes}
            />
          </div>

          {/* Task calendar filter (AC5) */}
          {calFilterTaskOptions.length > 0 && (
            <div className="flex flex-col gap-1">
              <Label htmlFor={calFilterTaskControlId} className="text-xs text-muted-foreground">
                {t('reports.maintenanceLogs.filterTask')}
              </Label>
              <TaskFilter
                id={calFilterTaskControlId}
                options={calFilterTaskOptions}
                selectedIds={selectedCalTaskIds}
                onApply={onCalTaskIdsApply}
              />
            </div>
          )}

          <Separator />

          {/* Shared calendar grid + dialogs + sign-off history.
              key={selectedInstrumentId} forces a remount on instrument change so the
              panel's local task multi-select (selectedTaskIds) resets instead of
              carrying over task ids from the previously selected instrument into the
              cal_task_ids payload (see Reports/Show.tsx for the same pattern). */}
          <InstrumentCalendarViewPanel
            // Key includes calendarCurrentMonth so the panel remounts when the user
            // navigates months — this resets internal task-select state and ensures
            // initialCalendarData for the new month is applied cleanly.
            key={`${selectedInstrumentId}-${calendarCurrentMonth}`}
            instrumentId={selectedInstrumentId}
            instrumentLabel={summary.instrumentLabel}
            serialNumber={summary.serialNumber}
            initialCalendarData={calendarByMonth.get(calendarCurrentMonth) ?? null}
            fetchCalendarData={fetchCalendarData}
            alertOnlyAffectsCalendar={false}
            pageLevelTaskIds={selectedCalTaskIds}
            // Maintenance Logs renders the same TaskFilter above; hide the panel's own so
            // each control has one owner.
            hideTaskFilter
            // The unified History list below owns the toggle — suppress the
            // duplicate CalendarListView + AlertOnlyToggle inside the panel.
            hideListView
            // Lock the panel to the current month — pickers and arrows are hidden;
            // month navigation is handled by the page-level ‹/› buttons above.
            lockedToMonth={calendarCurrentMonth}
            onAfterReview={() => router.reload({ only: ['activityFeed', 'summary'] })}
          />
        </div>
      )}

      {/* ── Supplies Summary section (AC4; legacy Controls/Reagents) ── */}
      {selectedInstrumentId && currentLots !== null && (
        <div className="rounded-md border">
          <button
            type="button"
            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold hover:bg-muted/30"
            onClick={() => setLotSummaryOpen((open) => !open)}
            aria-expanded={lotSummaryOpen}
          >
            {t('reports.maintenanceLogs.lotSummary.title')}
            {lotSummaryOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {lotSummaryOpen && (
            <div className="border-t px-4 py-3">
              {currentLots.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('reports.maintenanceLogs.lotSummary.empty')}
                </p>
              ) : (
                <div className="space-y-4">
                  {currentLots.map((group) => (
                    <div key={group.lotId}>
                      <p className="text-sm mb-1">
                        <span className="text-muted-foreground mr-1">
                          {t('reports.maintenanceLogs.lotSummary.controlsReagents')}
                        </span>
                        <span className="font-semibold">{group.lotName}</span>
                      </p>
                      <div className="divide-y rounded-md border">
                        {group.lotDetails.map((detail) => (
                          <div
                            key={detail.id}
                            className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
                          >
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                              {detail.createdOn && (
                                <span className="text-muted-foreground">
                                  {formatCalendarDate(detail.createdOn)}
                                </span>
                              )}
                              <span>
                                <span className="font-medium">{detail.lotNumber}</span>
                                {' – '}
                                <span className={detail.expiresSoon ? 'text-destructive' : ''}>
                                  {detail.expirationDate
                                    ? `Expires ${formatCalendarDate(detail.expirationDate)}`
                                    : 'No Expiration'}
                                </span>
                              </span>
                              {(detail.manufacturer || detail.product) && (
                                <span className="text-xs text-muted-foreground">
                                  {[detail.manufacturer, detail.product].filter(Boolean).join(' ')}
                                </span>
                              )}
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="shrink-0"
                              onClick={() =>
                                setLotDetailModal({ ...detail, lotName: group.lotName })
                              }
                            >
                              {t('reports.maintenanceLogs.lotSummary.viewDetails')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Lot Detail Modal ── */}
      <Dialog
        open={lotDetailModal !== null}
        onOpenChange={(open) => {
          if (!open) setLotDetailModal(null)
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col">
          <DialogHeader>
            <DialogTitle>
              {lotDetailModal?.lotName ?? t('reports.maintenanceLogs.lotSummary.viewDetails')}
            </DialogTitle>
          </DialogHeader>
          {lotDetailModal && (
            <div className="overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.lotNumber')}
                  value={lotDetailModal.lotNumber}
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.dateReceived')}
                  value={lotDetailModal.dateReceived}
                  date
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.expires')}
                  value={lotDetailModal.expirationDate}
                  date
                  urgent={lotDetailModal.expiresSoon}
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.dateOpened')}
                  value={lotDetailModal.dateOpened}
                  date
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.openedExpiration')}
                  value={lotDetailModal.openedExpiration}
                  date
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.dateInUse')}
                  value={lotDetailModal.dateInUse}
                  date
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.otherIdentifier')}
                  value={lotDetailModal.otherIdentifier}
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.dateDiscarded')}
                  value={lotDetailModal.dateDiscarded}
                  date
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.manufacturer')}
                  value={lotDetailModal.manufacturer}
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.product')}
                  value={lotDetailModal.product}
                />
                <LotField
                  label={t('reports.maintenanceLogs.lotSummary.description')}
                  value={lotDetailModal.description}
                />
              </div>
              {(lotDetailModal.lotTaskDataFields as LotTaskDataField[]).length > 0 && (
                <div className="mt-3 overflow-hidden rounded-md border">
                  {(lotDetailModal.lotTaskDataFields as LotTaskDataField[]).map((field, index) => {
                    const visibleValues = visibleLotDataFieldValues(
                      field.dataFieldType,
                      field.existingValues,
                    )
                    return (
                      <div key={index} className="border-b last:border-b-0">
                        <div className="flex items-center gap-1.5 bg-muted px-3 py-2 text-sm font-semibold">
                          <span>{field.lotName}</span>
                          <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
                          <span className="font-normal text-muted-foreground">
                            {t('instruments.lotsForm.linkedToTask')}
                          </span>
                          <span>{field.taskName}</span>
                        </div>
                        <div className="space-y-1 px-3 py-2">
                          <p className="text-xs text-muted-foreground">
                            {t('instruments.lotsForm.dataFieldValuesTitle')}
                          </p>
                          {visibleValues.length > 0 ? (
                            visibleValues.map((v, vi) => (
                              <div key={vi} className="flex items-center gap-6 py-0.5 text-sm">
                                <span className="font-semibold">{field.dataFieldLabel}</span>
                                <span className="text-muted-foreground">
                                  {v.operatorType && <span className="mr-1">{v.operatorType}</span>}
                                  {v.value}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center gap-6 py-0.5 text-sm">
                              <span className="font-semibold">{field.dataFieldLabel}</span>
                              <span className="italic text-muted-foreground">
                                {t('instruments.lotsForm.noDataFieldValuesConfigured')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Unified chronological history — activity feed is the sole list ── */}
      {selectedInstrumentId && (
        <MaintenanceLogsActivityFeed
          instrumentId={selectedInstrumentId}
          activityFeed={activityFeed}
          alertOnly={alertOnly}
          onAlertOnlyChange={setAlertOnly}
        />
      )}

      {selectedInstrumentId && (
        <div className="flex justify-center pt-2">{instrumentStepButtons}</div>
      )}
    </div>
  )
}

export default MaintenanceLogsReportPage
